// Single-pass class-variable transformer entry point.
// It combines composition expansion and direct class-var replacement.
import { splitLines, splitTopLevelStatements } from './lines'
import { transformCompositionStatement } from './composition'
import { replaceClassVariableReferencesInText } from './replacements'
import { replaceStructuredVarReferencesInText } from './reference-values'
import { resolveSelectorReferencesInSource } from './selector-source'
import type { ClassTransformOptions } from './types'

// Transform source once by handling compositions and class var references together.
export function transformSourceWithClassVariables(source: string, options: ClassTransformOptions): string {
  // Return early when source has no var() references to transform.
  if (!source.includes('var(')) {
    return source
  } else {
    // Continue class-variable transformation pipeline.
  }

  const lines = splitLines(source)
  const outputLines: string[] = []

  // Process each line independently to preserve existing source ordering.
  for (const line of lines) {
    outputLines.push(transformLine(line, options))
  }

  const transformedSource = outputLines.join('\n')
  // Return source with selector-level var() references resolved.
  return resolveSelectorReferencesInSource(transformedSource, options)
}

// Transform one line with top-level statement awareness.
function transformLine(line: string, options: ClassTransformOptions): string {
  // Skip work quickly when line does not contain class-variable references.
  if (!line.includes('var(')) {
    return line
  } else {
    // Continue statement-level transformation.
  }

  // Use fast path for single-statement lines without top-level semicolons.
  if (!line.includes(';')) {
    const composition = transformCompositionStatement(line, options)
    if (composition) {
      return composition
    } else {
      // Continue direct replacement in fast single-statement mode.
    }

    // Return direct replacement result for single-statement line.
    return replaceStructuredVarReferencesInText(
      replaceClassVariableReferencesInText(line, options.classVariables),
      options,
    )
  } else {
    // Continue multi-statement parsing for semicolon-delimited lines.
  }

  const parts = splitTopLevelStatements(line)
  const transformedParts: string[] = []
  let hasAnyClassChange = false

  // Transform every statement part and keep original terminator behavior.
  for (const part of parts) {
    const composition = transformCompositionStatement(part.text, options)
    if (composition) {
      transformedParts.push(composition)
      hasAnyClassChange = true
      continue
    } else {
      // Continue direct replacement when statement is not composition.
    }

    const replaced = replaceStructuredVarReferencesInText(
      replaceClassVariableReferencesInText(part.text, options.classVariables),
      options,
    )
    // Track whether current part changed via direct class-variable replacement.
    if (replaced !== part.text) {
      hasAnyClassChange = true
    } else {
      // Keep change flag as-is.
    }

    const suffix = part.hasTerminator ? ';' : ''
    transformedParts.push(`${replaced}${suffix}`)
  }

  // Keep original line untouched when no class transformation happened.
  if (!hasAnyClassChange) {
    return line
  } else {
    // Return statement-expanded line output.
    return transformedParts.join('\n')
  }
}
